import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Anchor,
  Box,
  Button,
  Card,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { LoginBackground } from './LoginBackground';
import { VdzLogo } from '../components/VdzLogo';
import classes from './LoginPage.module.css';

const loginSchema = z.object({
  email: z.string().email('Ungültige E-Mail-Adresse'),
  password: z.string().min(1, 'Passwort ist erforderlich'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: { email: '', password: '' },
    validate: schemaResolver(loginSchema, { sync: true }),
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      await login(values);
      navigate('/dashboard');
    } catch {
      // Error handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className={classes.pageRoot}
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        background:
          'linear-gradient(0deg, var(--mantine-color-vdzGray-5) 0%, var(--mantine-color-vdzGray-2) 75%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--mantine-spacing-md)',
      }}
    >
      <LoginBackground />

      <Stack
        gap="xl"
        align="center"
        style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}
      >
        <Stack gap="xs" align="center" className={classes.logoBlock}>
          <VdzLogo width={96} color="var(--mantine-color-vdzGray-8)" />
          <Title order={2} fw={700} c="black">
            Meldeplattform
          </Title>
        </Stack>

        <Card
          padding="xl"
          radius="md"
          withBorder
          shadow="md"
          style={{ width: '100%' }}
          className={classes.cardBlock}
        >
          <Stack gap="lg">
            <Title order={3}>
              Anmelden
            </Title>

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <TextInput
                  label="E-Mail-Adresse"
                  placeholder="name@vdzm.de"
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  label="Passwort"
                  placeholder="••••••••"
                  {...form.getInputProps('password')}
                />
                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  mt="xs"
                  color="vdzGray.8"
                >
                  Anmelden
                </Button>
              </Stack>
            </form>

            <Anchor
              href="#"
              size="sm"
              c="vdzGray.7"
              ta="center"
              onClick={(e) => e.preventDefault()}
            >
              Passwort vergessen?
            </Anchor>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
}
